import { useState } from 'react';

import { BsKey, BsPlus } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import EmptyState from '#globalComponents/EmptyState/EmptyState';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';

import { useRevokeApiKey } from '../../models/apiKeys/mutations';
import { useApiKeys } from '../../models/apiKeys/queries';

import ApiKeyListItem from './ApiKeyListItem';
import CreateApiKeyModal from './CreateApiKeyModal';

// The API docs live on the backend host, not the frontend host — build the absolute URL
// from the same base as backendFetcher so the link works locally and in production.
const API_DOCS_URL = `${process.env.REACT_APP_BACKEND_WORKER_URL}/api/docs`;

export const ApiKeysSettings = () => {
	const { data: apiKeys = [], isLoading, error } = useApiKeys();
	const revokeKey = useRevokeApiKey();
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [keyToRevoke, setKeyToRevoke] = useState(null);

	const openCreate = () => setIsCreateOpen(true);

	const handleRevoke = async () => {
		if (!keyToRevoke) return;
		await revokeKey.mutateAsync(keyToRevoke.id);
		setKeyToRevoke(null);
	};

	return (
		<SectionCard
			icon={BsKey}
			title="API-Schlüssel"
			subtitle="Erstellen Sie Schlüssel für den programmatischen Zugriff auf Ihre Daten."
			headerAction={
				<Button variant="solid" size="sm" onClick={openCreate}>
					<BsPlus className="h-4 w-4" />
					Schlüssel erstellen
				</Button>
			}
		>
			<p className="mb-4 text-sm text-bgBase-800">
				Vollständige Dokumentation:{' '}
				<a
					href={API_DOCS_URL}
					target="_blank"
					rel="noreferrer"
					className="font-medium text-primary-600 underline"
				>
					API-Referenz
				</a>
			</p>

			{isLoading ? (
				<div className="flex justify-center py-12">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
				</div>
			) : error ? (
				<Alert tone="warning">Die API-Schlüssel konnten nicht geladen werden.</Alert>
			) : apiKeys.length === 0 ? (
				<EmptyState
					icon={BsKey}
					title="Noch keine API-Schlüssel"
					description="Erstellen Sie Ihren ersten Schlüssel, um die API zu nutzen."
					action={<Button onClick={openCreate}>Ersten Schlüssel erstellen</Button>}
				/>
			) : (
				<div className="space-y-3">
					{apiKeys.map((key) => (
						<ApiKeyListItem key={key.id} apiKey={key} onRevoke={setKeyToRevoke} />
					))}
				</div>
			)}

			<CreateApiKeyModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />

			<ConfirmModal
				isOpen={Boolean(keyToRevoke)}
				onClose={() => setKeyToRevoke(null)}
				onConfirm={handleRevoke}
				title="API-Schlüssel widerrufen?"
				message={`Der Schlüssel "${keyToRevoke?.name ?? ''}" wird sofort ungültig. Anwendungen, die ihn verwenden, verlieren den Zugriff.`}
				confirmText="Widerrufen"
				isLoading={revokeKey.isPending}
			/>
		</SectionCard>
	);
};
